import { useEffect, useState } from 'react';
import { NameEmailAvatar } from '../../../../../ui/components/Common/NameEmailAvatar';
import { ClipBoardIcon, ClockIcon, TrashIcon } from '../../../../../ui/icons/icons';
import { Applicant } from '../../../../../utils/types/JobOpenings';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CloseRounded,
  EmailOutlined,
  InfoOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';

interface UnsuccessfulEmailConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedApplicants: Applicant[];
  loading?: boolean;
  onRemoveApplicant: (userId: string) => void;
  onClearAll?: () => void;
}

export const UnsuccessfulEmailConfirmationModal: React.FC<UnsuccessfulEmailConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedApplicants,
  loading = false,
  onRemoveApplicant,
  onClearAll,
}) => {
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  // Reset checkbox when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationChecked(false);
    }
  }, [isOpen]);

  // Filter to only show unsuccessful-pending applicants
  const pendingApplicants = selectedApplicants.filter(
    (applicant) => applicant.applicantStatus === 'Unsuccessful-Pending' && applicant.email,
  );

  const isLargeBatch = pendingApplicants.length > 50;

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          maxWidth: 896,
        },
      }}
    >
      {/* Header section with gradient */}
      <Box
        sx={{
          background: 'linear-gradient(to right, #ef4444, #dc2626)',
          px: 3,
          py: 2,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
              }}
            >
              <EmailOutlined sx={{ fontSize: 20, color: 'white' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'white' }}>
                Email Confirmation Required
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#fecaca' }}>
                Review candidates before sending unsuccessful emails
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            disabled={loading}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(4px)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              '&:disabled': { opacity: 0.5, color: 'white' },
            }}
          >
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Large batch warning */}
        {isLargeBatch && (
          <Box sx={{ px: 3, py: 2 }}>
            <Box
              sx={{
                bgcolor: '#fffbeb',
                borderLeft: '2px solid #fbbf24',
                p: 1.5,
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: '#fef3c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    borderRadius: '4px',
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 16, color: '#d97706' }} />
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <WarningAmberRounded sx={{ fontSize: 16, color: '#92400e' }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
                      Large Batch ({pendingApplicants.length} candidates)
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: '12px', color: '#b45309' }}>
                    Processing may take several minutes. Please keep this window open.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}

        {pendingApplicants.length === 0 ? (
          <Box sx={{ px: 3, pb: 2 }}>
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                bgcolor: '#f9fafb',
                borderRadius: '8px',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '8px',
                }}
              >
                <EmailOutlined sx={{ fontSize: 24, color: '#9ca3af' }} />
              </Box>
              <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#111827', mb: 0.5 }}>
                No Eligible Candidates
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#4b5563',
                  maxWidth: 384,
                  mx: 'auto',
                }}
              >
                No unsuccessful-pending candidates with email addresses found in your selection.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ px: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ClipBoardIcon size={16} />
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                    Selected Candidates
                  </Typography>
                  <Chip
                    size="small"
                    label={`${pendingApplicants.length} pending`}
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      bgcolor: '#dbeafe',
                      color: '#1e40af',
                      height: 22,
                    }}
                  />
                </Stack>
                {pendingApplicants.length > 0 && (
                  <Typography
                    component="button"
                    onClick={onClearAll}
                    disabled={loading}
                    sx={{
                      fontSize: '13px',
                      color: '#dc2626',
                      fontWeight: 500,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      p: 0,
                      transition: 'color 0.2s',
                      '&:hover': { color: '#991b1b' },
                      '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                    }}
                  >
                    Clear All
                  </Typography>
                )}
              </Stack>

              <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                <Stack spacing={1}>
                  {pendingApplicants.map((applicant) => (
                    <Box
                      key={applicant.userId}
                      sx={{
                        p: 1.5,
                        transition: 'all 0.2s',
                        borderRadius: '4px',
                        '&:hover': {
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          '& .remove-btn': { opacity: 1 },
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                          }}
                        >
                          <NameEmailAvatar
                            name={applicant.name}
                            email={applicant.email}
                            avatar=""
                          />
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1.5 }}>
                            <Chip
                              size="small"
                              label={
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      bgcolor: '#facc15',
                                      borderRadius: '2px',
                                    }}
                                  />
                                  <span>{applicant.applicantStatus}</span>
                                </Stack>
                              }
                              sx={{
                                fontSize: '11px',
                                fontWeight: 500,
                                bgcolor: '#fef9c3',
                                color: '#854d0e',
                                height: 24,
                              }}
                            />
                            <IconButton
                              className="remove-btn"
                              onClick={() => onRemoveApplicant(applicant.userId)}
                              disabled={loading}
                              size="small"
                              title="Remove candidate"
                              sx={{
                                p: 0.5,
                                color: '#9ca3af',
                                opacity: 0,
                                transition: 'opacity 0.2s, color 0.2s',
                                '&:hover': { color: '#dc2626' },
                              }}
                            >
                              <TrashIcon size={16} />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>

            {/* Compact confirmation section */}
            <Box sx={{ bgcolor: '#f9fafb', p: 2, borderRadius: '8px', mb: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box sx={{ flexShrink: 0, pt: 0.5 }}>
                  <Checkbox
                    checked={confirmationChecked}
                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                    disabled={loading}
                    size="small"
                    sx={{
                      p: 0,
                      color: '#d1d5db',
                      '&.Mui-checked': { color: '#dc2626' },
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827', mb: 0.5 }}>
                    I understand this action cannot be undone
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#4b5563' }}>
                    I confirm that I want to send unsuccessful emails to
                    <Typography component="span" sx={{ fontWeight: 600, color: '#111827' }}>
                      {' '}
                      {pendingApplicants.length} candidate
                      {pendingApplicants.length !== 1 ? 's' : ''}
                    </Typography>
                    {' '}and permanently change their status to &quot;Unsuccessful-Closed&quot;.
                    {isLargeBatch && (
                      <Typography
                        component="span"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: '#d97706',
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                          <ClockIcon size={12} />
                          <span>
                            This large batch may take several minutes to process.
                          </span>
                        </Stack>
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Action buttons */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: '#f9fafb',
          borderTop: '1px solid',
          borderColor: 'grey.200',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
          {pendingApplicants.length > 0 && (
            <span>
              Ready to send to {pendingApplicants.length} candidate
              {pendingApplicants.length !== 1 ? 's' : ''}
            </span>
          )}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              color: '#374151',
              borderColor: '#d1d5db',
              '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' },
              '&:disabled': { opacity: 0.5 },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading || pendingApplicants.length === 0 || !confirmationChecked}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <EmailOutlined sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              textTransform: 'none',
              background: 'linear-gradient(to right, #ef4444, #dc2626)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              '&:hover': {
                background: 'linear-gradient(to right, #dc2626, #b91c1c)',
                boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
              },
              '&:disabled': {
                opacity: 0.5,
                background: 'linear-gradient(to right, #ef4444, #dc2626)',
                color: 'white',
              },
            }}
          >
            {loading
              ? isLargeBatch
                ? `Processing ${pendingApplicants.length} emails...`
                : 'Sending emails...'
              : `Send Emails (${pendingApplicants.length})`}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
